import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// ── GEA BRAND PALETTE ─────────────────────────────────────────────────────────
const GEA_BLUE: [number, number, number] = [0, 0, 169];     // #0000a9
const GEA_SLATE: [number, number, number] = [30, 41, 59];    // slate-800
const GEA_LIGHT: [number, number, number] = [241, 245, 249]; // slate-100
const GEA_MID: [number, number, number] = [148, 163, 184]; // slate-400
const GEA_DARK: [number, number, number] = [15, 23, 42];    // slate-900
const WHITE: [number, number, number] = [255, 255, 255];
const AMBER: [number, number, number] = [245, 158, 11];
const RED: [number, number, number] = [185, 28, 28];

const setupDoc = () => new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', putOnlyUsedFonts: true });

const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
  });
};

const addAppScreenshot = (doc: jsPDF, imageData: string, y: number, caption: string) => {
  const imgW = 180;
  const imgH = 90; // Aspect ratio approx 2:1
  doc.addImage(imageData, 'PNG', 15, y, imgW, imgH);
  doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.5); doc.rect(15, y, imgW, imgH);
  doc.setFontSize(7); doc.setTextColor(...GEA_MID);
  doc.text(`FIG: ${caption.toUpperCase()}`, 15, y + imgH + 4);
  return y + imgH + 12;
};

const triggerDownload = async (doc: jsPDF, filename: string) => {
  try {
    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);

    // Using programmatically created link to bypass some popup-blocking window behavior
    const link = document.createElement('a');
    link.href = blobUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) { console.error("[GEA-NDT] Manual export error:", err); }
};

// ── GEA STANDARD HEADER ───────────────────────────────────────────────────────
const addHeader = (doc: jsPDF, title: string, pageLabel: string) => {
  // Dark slate bar
  doc.setFillColor(...GEA_SLATE); doc.rect(0, 0, 210, 28, "F");
  // GEA blue logo block
  doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 28, 28, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...WHITE);
  doc.text("GEA", 14, 17, { align: "center" });
  // Title
  doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(...WHITE);
  doc.text("NDT-CHECK PRO  |  GEA Engineering Services", 34, 12);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...GEA_MID);
  doc.text(`${title.toUpperCase()}  ·  ${pageLabel}`, 34, 19);
  // Bottom accent
  doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.5); doc.line(0, 28, 210, 28);
};

// ── GEA STANDARD FOOTER ───────────────────────────────────────────────────────
const addFooter = (doc: jsPDF, pageNum: number) => {
  doc.setFillColor(...GEA_LIGHT); doc.rect(0, 283, 210, 14, "F");
  doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.4); doc.line(0, 283, 210, 283);
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...GEA_MID);
  doc.text("GEA Group  ·  NDT-CHECK PRO V6.0  ·  Regulatory Compliance Document", 15, 289);
  doc.setFont("helvetica", "bold"); doc.setTextColor(...GEA_BLUE);
  doc.text(`SHEET ${pageNum}`, 195, 289, { align: "right" });
};

const addSectionTitle = (doc: jsPDF, title: string, y: number) => {
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...GEA_BLUE);
  doc.text(title, 15, y);
  doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(0.5); doc.line(15, y + 2, 195, y + 2);
  return y + 10;
};

const addParagraph = (doc: jsPDF, text: string, y: number, fontSize = 10) => {
  doc.setFont("helvetica", "normal"); doc.setFontSize(fontSize); doc.setTextColor(...GEA_DARK);
  const lines = doc.splitTextToSize(text, 180);
  doc.text(lines, 15, y);
  return y + (lines.length * (fontSize * 0.55));
};

// ── GEA COVER PAGE helper ─────────────────────────────────────────────────────
const addGeaCover = (doc: jsPDF, mainTitle: string, subtitle: string, tagline: string) => {
  // Full dark slate cover
  doc.setFillColor(...GEA_SLATE); doc.rect(0, 0, 210, 297, "F");
  // Top GEA banner strip
  doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 210, 65, "F");
  // GEA white logo box
  doc.setFillColor(...WHITE); doc.rect(15, 12, 45, 40, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(28); doc.setTextColor(...GEA_BLUE);
  doc.text("GEA", 37.5, 38, { align: "center" });
  // Title
  doc.setFont("helvetica", "bold"); doc.setFontSize(26); doc.setTextColor(...WHITE);
  doc.text(mainTitle, 105, 95, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(13); doc.setTextColor(...GEA_MID);
  doc.text(subtitle, 105, 107, { align: "center" });
  // Divider
  doc.setDrawColor(...GEA_BLUE); doc.setLineWidth(1); doc.line(50, 115, 160, 115);
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...GEA_MID);
  doc.text(tagline, 105, 122, { align: "center" });
  // Footer text
  doc.setFontSize(7.5); doc.setFont("helvetica", "italic"); doc.setTextColor(...GEA_MID);
  doc.text("PROPRIETARY & CONFIDENTIAL — GEA Group · Authorised Personnel Only", 105, 272, { align: "center" });
  doc.text("ISO 9712 | ISO 5817 | NEN 2767 ALIGNED", 105, 280, { align: "center" });
};

// ── CALLOUT BOX ───────────────────────────────────────────────────────────────
const addCallout = (doc: jsPDF, type: 'INFO' | 'WARNING' | 'CRITICAL', title: string, body: string, y: number) => {
  const color = type === 'INFO' ? GEA_BLUE : type === 'WARNING' ? AMBER : RED;
  doc.setFillColor(...GEA_LIGHT); doc.setDrawColor(...color);
  const bodyLines = doc.splitTextToSize(body, 165);
  const h = 18 + (bodyLines.length * 4.5);
  doc.rect(15, y, 180, h, "FD");
  doc.setLineWidth(2); doc.line(15, y, 15, y + h);
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...color);
  doc.text(`${type}: ${title}`, 20, y + 8);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...GEA_DARK);
  doc.text(bodyLines, 20, y + 15);
  return y + h + 8;
};

// ── TABLE DEFAULTS ────────────────────────────────────────────────────────────
const geaTableStyles = { headStyles: { fillColor: GEA_BLUE, textColor: WHITE, fontStyle: 'bold' as const }, alternateRowStyles: { fillColor: GEA_LIGHT } };

// =============================================================================
// REGULATIONS PDF — NOW V6.0 NEURAL STANDARDS
// =============================================================================
export function generateRegulationsPdf(projectName?: string, customerName?: string) {
  const doc = new jsPDF();
  let p = 1;

  // Cover
  addGeaCover(doc, "AI NEURAL & VISUAL STANDARDS", "Visual Engineering, Compliance & Deep Reasoning", "Official Technical Whitepaper V6.0");

  // Page 2: Multi-Model Architecture
  doc.addPage();
  addHeader(doc, "Neural Frameworks", "AI_TECH_V60_01");
  let y = addSectionTitle(doc, "1.0 Multi-Model Neural Architecture", 45);
  y = addParagraph(doc, "NDT-CHECK PRO V6.0 utilizes a sophisticated tiered AI strategy. The system dynamically orchestrates multiple neural engines to balance sub-millimeter precision with extreme low-latency operational requirements.", y);

  autoTable(doc, {
    startY: y + 5,
    head: [['Model Tier', 'Technology', 'Mission Function']],
    body: [
      ['Tier 0: Reasoning', 'Gemini 3.5 Pro (Ultra)', 'Advanced RAG-based analysis, complex triangulation, and final certification logic.'],
      ['Tier 1: Analytical', 'Gemini 2.5 Pro Vision', 'Primary defect discovery, precise bounding-box anchoring, and material classification.'],
      ['Tier 2: Real-time', 'Gemini 2.5 Flash', 'Low-latency live stream inference, verbal anomaly feedback, and rapid scrubbing support.'],
      ['Tier 3: Geometric', 'Lidar SLAM Fusion', 'X/Y/Z localization of defects within the Digital Twin volume (Elios 3 Native).'],
      ['Tier 4: Temporal', 'Parallax Engine V2', 'Frame-to-frame consistency checks to eliminate optical artifacts (glare & reflections).'],
      ['Tier 5: Linguistic', 'Neural Audio Assist', 'Real-time text-to-speech feedback for hands-free inspector safety protocols.']
    ],
    theme: 'grid',
    ...geaTableStyles
  });

  y = (doc as any).lastAutoTable.finalY + 15;
  addSectionTitle(doc, "2.0 Parallax Verification Logic", y);
  addParagraph(doc, "Unlike standard object detection, our 'Temporal Parallax' algorithm requires 3-frame consistency. If a shadow does not move in perfect sync with the calculated camera extrinsic vector, it is classified as a surface stain or reflection and suppressed from the audit ledger. This ensures a False Positive Rate (FPR) of < 0.2% in polished stainless steel environments.", y + 10);
  addFooter(doc, p++);

  // Page 3: Visual & Neural Standards (NEW)
  doc.addPage();
  addHeader(doc, "Neural Detection Standards", "NDT_STANDARDS_02");
  y = addSectionTitle(doc, "3.0 Confidence & Severity Weighting", 45);
  y = addParagraph(doc, "All neural findings are subject to a dual-weighting protocol. Detections are ranked by both 'Geometric Certainty' and 'Structural Risk'.", y);

  autoTable(doc, {
    startY: y + 5,
    head: [['Confidence Score', 'Classification', 'System Action']],
    body: [
      ['90% - 100%', 'Certified Detection', 'Automated entry to Project Ledger; High-priority notification.'],
      ['70% - 89%', 'Probable Defect', 'Flagged for Human-in-the-Loop (HITL) verification.'],
      ['50% - 69%', 'Advisory Signal', 'Logged as surface texture anomaly; Hidden from non-technical reports.'],
      ['< 50%', 'Neural Noise', 'Suppressed instantly by the Parallax Engine.']
    ],
    theme: 'striped',
    ...geaTableStyles
  });

  y = (doc as any).lastAutoTable.finalY + 15;
  addSectionTitle(doc, "4.0 Minimum Detectable Feature Size (MDFS)", y);
  addParagraph(doc, "The resolving power of the Neural Engine is tethered to the Ground Sampling Distance (GSD). At a standard 0.5m standoff with 4K RGB optics, the NDT-CHECK PRO system adheres to the following MDFS standards:", y + 10);

  autoTable(doc, {
    startY: y + 25,
    head: [['Defect Class', 'Min. Pixel Resolution', 'Physical MDFS (Approx.)']],
    body: [
      ['Linear Cracks', '5px Lateral width', '0.05 mm'],
      ['Pitting / Corrosion', '15px Diameter', '0.12 mm'],
      ['Dents / Deformations', '50px Clustered', '0.50 mm'],
      ['Residue / Fouling', '120px Area', '1.50 mm']
    ],
    theme: 'grid',
    ...geaTableStyles
  });
  addFooter(doc, p++);

  // Page 4: ISO & NEN
  doc.addPage();
  addHeader(doc, "Regulatory Mapping", "REG_COMP_03");
  y = addSectionTitle(doc, "5.0 ISO 5817: Welding Standards", 45);
  y = addParagraph(doc, "The Weld Integrity protocol is mapped directly to ISO 5817 (Fusion-welded joints in steel). The AI categorizes findings into the following quality levels:", y);
  autoTable(doc, { startY: y + 5, head: [['Standard', 'AI Mapping', 'Action Required']], body: [['ISO 5817 (B)', 'Stringent: No porosity >0.5mm', 'Immediate repair required.'], ['ISO 5817 (C)', 'Intermediate: Minor undercut', 'Monitor in next 12-month cycle.'], ['ISO 5817 (D)', 'Moderate: Excess weld metal', 'Accepted as nominal.'], ['ISO 9712', 'Personnel Certification', 'AI acts as decision support for Level II/III.']], theme: 'striped', ...geaTableStyles });
  y = (doc as any).lastAutoTable.finalY + 15;
  addSectionTitle(doc, "6.0 NEN 2767: Condition Assessment", y);
  addParagraph(doc, "For general structural audits, findings are translated into NEN 2767 scores (1 through 6). Condition 1 is 'New', while Condition 6 is 'End of Life / Imminent Failure'.", y + 10);
  addFooter(doc, p++);

  // Page 5: TÜV & Safety
  doc.addPage();
  addHeader(doc, "Operational Safety", "SAFE_TUV_04");
  y = addSectionTitle(doc, "7.0 TÜV SÜD / Rheinland Alignment", 45);
  y = addParagraph(doc, "The Mission Planner and Telemetry Audit modules satisfy European technical monitoring requirements for autonomous indoor inspection systems.", y);
  autoTable(doc, { startY: y + 5, head: [['Safety Category', 'System Guardrail']], body: [['Battery RTH', 'Automated egress path triggered at 10% SOC.'], ['Signal Stabilizer', 'Live stream bitrate adjustment to maintain ISO 13849 link integrity.'], ['GDPR Compliance', 'Zero-Cloud architecture ensures local-only data residence.'], ['Verification', 'Multi-agent adversarial consensus ensures AI calibration.']], theme: 'grid', ...geaTableStyles });
  y = (doc as any).lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "bold"); doc.setTextColor(...AMBER); doc.setFontSize(9);
  doc.text("OFFICIAL DECLARATION:", 15, y);
  doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(...GEA_DARK);
  y = addParagraph(doc, "NDT-CHECK PRO V6.0 is a decision-support system. Final certification remains the legal responsibility of a certified Level III NDT Personnel. This document and its neural standards are subject to change as underlying models evolve.", y + 6);
  addFooter(doc, p++);

  triggerDownload(doc, "GEA_NDT_CHECK_PRO_V6.0_AI_Neural_Visual_Standards.pdf");
}

// =============================================================================
// USER MANUAL PDF
// =============================================================================
export const generateUserManual = async () => {
  const doc = setupDoc();
  let absolutePage = 1;

  // Pre-load assets
  const assets: Record<string, string> = {};
  try {
    assets.home = await loadImage('/assets/manual/home_screen.png');
    assets.video = await loadImage('/assets/manual/video_inspector.png');
    assets.planner = await loadImage('/assets/manual/mission_planner.png');
    assets.results = await loadImage('/assets/manual/analysis_results.png');
    assets.trajectory = await loadImage('/assets/manual/trajectory_map.png');
    assets.lab = await loadImage('/assets/manual/gea_lab.png');
  } catch (e) { console.warn("[GEA-NDT] Manual assets missing or failed to load", e); }

  // Cover
  addGeaCover(doc, "NDT-CHECK PRO", "TECHNICAL OPERATIONAL MANUAL", "BUILD V6.0 | GEA SECURE NEURAL FRAMEWORK");

  // Table of Contents
  doc.addPage();
  addHeader(doc, "Mission Index", "MANUAL_INDEX");
  let yPos = addSectionTitle(doc, "Table of Contents", 45);
  const toc = [
    ["PART I", "ENGINEERING DATA SERVICES (WORKFLOWS)", ""],
    ["Chapter 1", "Image Survey: High-Resolution Static Analysis", "Page 4"],
    ["Chapter 2", "Video Survey: Temporal Ingestion & Neural Sweep", "Page 5"],
    ["Chapter 3", "Elios 3 Spatial: 3D Digital Twin Synchronization", "Page 6"],
    ["Chapter 4", "Autonomy Planner: Mission Optimization & Safety", "Page 8"],
    ["Chapter 5", "Live Scanner: Real-time Edge Stream Inference", "Page 9"],

    ["PART II", "NEURAL DETECTION CORE (AI PROTOCOLS)", ""],
    ["Chapter 6", "Crack Detection: Fine Fissure Identification", "Page 10"],
    ["Chapter 7", "Weld Integrity: HAZ & Joint Quality Audit", "Page 11"],
    ["Chapter 8", "Structural Audit: Corrosion & Deformation", "Page 12"],
    ["Chapter 9", "Cleanliness: Hygiene & Bio-residue Scaling", "Page 13"],

    ["PART III", "DATA MANAGEMENT & REPORTING", ""],
    ["Chapter 10", "GEA Laboratory Workbench: Neural Training", "Page 14"],
    ["Chapter 11", "Technical Reporting & Global Glossary", "Page 16"],
    ["Appendix", "Operational Safety & Technical Handoff", "Page 18"]
  ];
  autoTable(doc, {
    startY: yPos,
    body: toc,
    theme: 'plain',
    styles: { fontSize: 9.5, cellPadding: 4, textColor: GEA_DARK },
    columnStyles: { 0: { fontStyle: 'bold', textColor: GEA_BLUE, cellWidth: 25 } },
    didParseCell: (data) => {
      if (data.row.cells[0].text[0].startsWith("PART")) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.textColor = GEA_BLUE; data.cell.styles.fontSize = 11; }
    }
  });
  addFooter(doc, absolutePage++);

  // PART I: DATA ENGINEERING SERVICES
  doc.addPage();
  doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 210, 297, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(40); doc.setTextColor(...WHITE);
  doc.text("PART I", 105, 120, { align: "center" });
  doc.setFontSize(18); doc.text("ENGINEERING DATA SERVICES", 105, 135, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text("Deployment Workflows & Mission Control", 105, 145, { align: "center" });

  // Ch1: Image Survey
  doc.addPage();
  addHeader(doc, "Image Survey", "CH_01");
  yPos = addSectionTitle(doc, "1.1 High-Resolution Static Analysis", 45);
  yPos = addParagraph(doc, "The Image Survey module is designed for laboratory-grade crack assessment using high-resolution static imagery (RAW/JPG). Each image is treated as a discrete engineering event.", yPos);

  if (assets.home) {
    yPos = addAppScreenshot(doc, assets.home, yPos, "Service Navigation Hub");
  }

  yPos = addCallout(doc, "INFO", "PROJECT SELECTION", "Before initializing any scanner, the system will prompt for a Project Number. This anchors all engineering findings to a specific asset vault.", yPos);
  addFooter(doc, absolutePage++);

  // Ch2: Video Survey
  doc.addPage();
  addHeader(doc, "Video Survey", "CH_02");
  yPos = addSectionTitle(doc, "2.1 Temporal Ingestion & Neural Sweep", 45);
  yPos = addParagraph(doc, "The Video Survey module processes mission footage frame-by-frame. It utilizes the Parallax Engine to eliminate optical artifacts and ensure detection consistency across multiple viewing angles.", yPos);

  if (assets.video) {
    yPos = addAppScreenshot(doc, assets.video, yPos, "Video Inspector Interface");
  }

  yPos = addCallout(doc, "INFO", "CONSENSUS LOGIC", "AI requires 3-frame confirmation before a finding is logged to the ledger. This drastically reduces false positives from dust, glare, or rapid camera movement.", yPos);
  addFooter(doc, absolutePage++);

  // Ch3: Elios 3 Spatial
  doc.addPage();
  addHeader(doc, "Elios 3 Spatial", "CH_03");
  yPos = addSectionTitle(doc, "3.1 3D Digital Twin Synchronization", 45);
  yPos = addParagraph(doc, "The Spatial Review module provides a 3D Digital Twin of the inspection. Findings are anchored in X/Y/Z volume, allowing for precision repair targeting.", yPos);

  if (assets.trajectory) {
    yPos = addAppScreenshot(doc, assets.trajectory, yPos, "Spatial UI & Alignment Interface");
  }

  autoTable(doc, {
    startY: yPos + 5,
    head: [['Interaction', 'Operational Result']],
    body: [
      ['Click Marker', 'Instantly pivots the high-res video feed to the exact spatial finding.'],
      ['POC Align', 'Manual calibration of pointcloud vs. trajectory to eliminate drift.'],
      ['Hide Path', 'Toggles flight trajectory for unobstructed wall inspection.'],
      ['Repair PDF', 'One-click generation of coordinate-based repair location reports.']
    ],
    theme: 'grid',
    ...geaTableStyles
  });
  addFooter(doc, absolutePage++);

  // Ch4: Autonomy Planner
  doc.addPage();
  addHeader(doc, "Autonomy Planner", "CH_04");
  yPos = addSectionTitle(doc, "4.1 Mission Optimization & Safety", 45);
  yPos = addParagraph(doc, "The Autonomy Planner enables pre-mission waypoint sequencing in GPS-denied environments. It ensures 100% surface coverage by calculating optimal overlap for 4K sensors.", yPos);

  if (assets.planner) {
    yPos = addAppScreenshot(doc, assets.planner, yPos, "3D Mission Planner Interface");
  }

  yPos = addCallout(doc, "WARNING", "SAFETY PROTOCOLS", "Autonomous flight paths must be verified for clearance. The system enforces a 0.5m minimum buffer from structural walls.", yPos);
  addFooter(doc, absolutePage++);

  // Ch5: Live Scanner
  doc.addPage();
  addHeader(doc, "Live Scanner", "CH_05");
  yPos = addSectionTitle(doc, "5.1 Real-time Edge Stream Inference", 45);
  yPos = addParagraph(doc, "Live Scanner allows for parallel NDT inspection during active flight. It ingests low-latency RTSP streams and provides immediate verbal 'Anomaly Detected' alerts to the ground station crew.", yPos);
  yPos = addCallout(doc, "INFO", "VERBAL ASSIST", "The Neural Engine utilizes Tier-3 models for Live Sweep, prioritizing detection speed over deep classification during the live mission phase.", yPos);
  addFooter(doc, absolutePage++);

  // PART II: NEURAL DETECTION CORE
  doc.addPage();
  doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 210, 297, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(40); doc.setTextColor(...WHITE);
  doc.text("PART II", 105, 120, { align: "center" });
  doc.setFontSize(18); doc.text("NEURAL DETECTION CORE", 105, 135, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text("Inspection Personalities & Detection Protocols", 105, 145, { align: "center" });

  // Chapters 6-9: AI Protocols
  doc.addPage();
  addHeader(doc, "AI Protocols", "CH_06_09");
  yPos = addSectionTitle(doc, "Functional AI Profiles", 45);
  yPos = addParagraph(doc, "NDT-CHECK PRO V6.0 allows inspectors to switch 'Neural Protocols' on the fly. This changes the AI's internal taxonomy and detection weighting based on the specific asset class.", yPos);

  autoTable(doc, {
    startY: yPos + 5,
    head: [['Protocol', 'Focus & ISO Standard']],
    body: [
      ['Chapter 6: Crack Detection', 'Linear fissures, separation, stress points (ISO 5817-100).'],
      ['Chapter 7: Weld Integrity', 'Undercut, porosity, lack of fusion (ISO 5817-400). HAZ focus.'],
      ['Chapter 8: Structural Audit', 'Pitting, corrosion, deformation (NEN 2767). Long-term degradation.'],
      ['Chapter 9: Cleanliness', 'Biological residue, calcification, fouling (Food Grade). CIP audit.']
    ],
    theme: 'striped',
    ...geaTableStyles
  });
  addFooter(doc, absolutePage++);

  // PART III: DATA MANAGEMENT
  doc.addPage();
  doc.setFillColor(...GEA_BLUE); doc.rect(0, 0, 210, 297, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(40); doc.setTextColor(...WHITE);
  doc.text("PART III", 105, 120, { align: "center" });
  doc.setFontSize(18); doc.text("DATA MANAGEMENT & REPORTING", 105, 135, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.text("Training, Administration & Ground Truth", 105, 145, { align: "center" });

  // Ch10: Lab Workbench
  doc.addPage();
  addHeader(doc, "Laboratory Workbench", "CH_10");
  yPos = addSectionTitle(doc, "10.1 Ground Truth Neural Training", 45);
  yPos = addParagraph(doc, "The Lab Workbench is where human intelligence meets machine learning. Inspectors calibrate AI findings to create facility-specific datasets.", yPos);

  if (assets.lab) {
    yPos = addAppScreenshot(doc, assets.lab, yPos, "GEA Laboratory Workbench (Active Calibration)");
  }

  yPos = addCallout(doc, "INFO", "COMMIT TO VAULT", "Using the Commit button mirrors verified findings to the Ground Truth directory, preparing the system for the next generation of supervised learning.", yPos);
  addFooter(doc, absolutePage++);

  // Ch11: Reporting
  doc.addPage();
  addHeader(doc, "Reporting & Glossary", "CH_11");
  yPos = addSectionTitle(doc, "11.1 Engineering Assessment Ledger", 45);
  yPos = addParagraph(doc, "The sidebar ledger provides immediate chronological access to all findings. Clicking any entry will instantly jump the high-res playback to the exact defect keyframe.", yPos);

  if (assets.results) {
    yPos = addAppScreenshot(doc, assets.results, yPos, "Active Mission Ledger (INNO-FLEXTANK)");
  }

  autoTable(doc, {
    startY: yPos + 5,
    head: [['Report Type', 'Description']],
    body: [
      ['AS/IS GEA Technical', 'Engineering-grade audit of all AI findings, including NAS metadata.'],
      ['Repair Locations', 'Coordinate-based map for maintenance teams (PDF/CSV).'],
      ['GDPR Data Flow', 'Privacy compliance report for facility security officers.']
    ],
    theme: 'striped',
    ...geaTableStyles
  });

  doc.addPage();
  addHeader(doc, "Interface Glossary", "CH_11_B");
  yPos = addSectionTitle(doc, "11.2 Control & Navigation Reference", 45);

  autoTable(doc, {
    startY: yPos,
    head: [['Button / Control', 'Functional Operational Logic']],
    body: [
      ['CENTER ALIGN', 'Pivots the 3D map to the absolute center of the asset geometry.'],
      ['HIDE PATH', 'Toggles UAV flight trajectory for better wall visibility.'],
      ['HIDE FRUSTRUMS', 'Removes camera cones from the 3D scene (Default: Hidden).'],
      ['WALL PROJECTION', 'Projects findings directly onto the vessel wall geometry.'],
      ['3D ALIGN', 'Enables precision nudge arrows for pointcloud/trajectory syncing.']
    ],
    theme: 'grid',
    ...geaTableStyles
  });
  addFooter(doc, absolutePage++);

  // Appendix/Legal
  doc.addPage();
  addHeader(doc, "Appendix", "APP_TECH");
  yPos = addSectionTitle(doc, "Technical Specifications", 45);
  autoTable(doc, {
    startY: yPos,
    head: [['Component', 'Standard / Value']],
    body: [
      ['AI Model', 'Gemini 3.0 Pro Vision (Complex Reasoning)'],
      ['Display Output', '4K / 2K / 1600x900 Optimization'],
      ['Video Encoder', 'H.264 / MediaRecorder API'],
      ['PDF Engine', 'jsPDF V2.5 / autoTable'],
      ['Encryption', 'AES-256 (Vault Mode Only)']
    ],
    theme: 'grid',
    ...geaTableStyles
  });
  yPos = 120;
  addSectionTitle(doc, "Official Operational Declaration", yPos);
  doc.text("This tool is classified as 'Decision Support Software'. Final certification remains the sole legal responsibility of the certified NDT Inspector. AI findings are advisor data to augment the human visual path.", 15, yPos + 10);
  addFooter(doc, absolutePage++);

  triggerDownload(doc, "GEA_NDT_CHECK_PRO_V6.0_Full_Operational_Manual.pdf");
};

// =============================================================================
// GDPR MANUAL
// =============================================================================
export const generateGDPRManual = () => {
  const doc = setupDoc();
  let absolutePage = 1;

  addGeaCover(doc, "DATA FLOW ARCHITECTURE", "GDPR & SECURE DATA HANDLING PROTOCOLS", "BUILD V6.0 | ZERO-CLOUD EDGE SYSTEM");

  // Page 2: Core Principles
  doc.addPage();
  addHeader(doc, "Privacy & Data Residency", "GDPR_SEC_01");
  let yPos = addSectionTitle(doc, "1.0 The Zero-Cloud Edge Principle", 45);
  yPos = addParagraph(doc, "NDT-CHECK PRO V6.0 operates on a strict 'Zero-Cloud' architecture. Unlike standard SaaS platforms, this application does not use a central database for your inspection media. The Synology NAS acts exclusively as a delivery mechanism for the static application bundle; it never receives or stores your video files unless you manually map a network drive for archival.", yPos);

  yPos = addSectionTitle(doc, "2.0 Local Memory Isolation", yPos + 10);
  yPos = addParagraph(doc, "When you load a 10GB drone flight, the data is stored in the browser's RAM (Blob Storage). It never leaves your machine. The browser creates a virtual sandbox where the video is processed frame-by-frame. This ensures that even if the internet connection is severed, the video remains securely on your local hardware.", yPos);

  yPos = addCallout(doc, "INFO", "TEMPORARY BUFFERING", "Frames extracted for AI analysis are held in volatile memory only. Once the mathematical JSON result (box coordinates) is returned, the source frame is immediately purged from the buffer to minimize memory footprint and maximize privacy.", yPos);

  addFooter(doc, absolutePage++);

  // Page 3: AI Processing & Storage
  doc.addPage();
  addHeader(doc, "AI Processing Lifecycle", "GDPR_SEC_02");
  yPos = addSectionTitle(doc, "3.0 The AI Data Cycle (In-Flight)", 45);
  yPos = addParagraph(doc, "While sensing is local, 'Deep Reasoning' happens via a secure API. However, the data payload is minimal and transient.", yPos);

  autoTable(doc, {
    startY: yPos + 5,
    head: [['Phase', 'Process', 'System / Location', 'Permanence']],
    body: [
      ['Data Ingestion', 'Loading Raw Video/Image', 'Local Browser Cache', 'Short-term (Session Only)'],
      ['Inference Prep', 'Extracting Keyframe Sample', 'Volatile RAM', 'Short-term (Milliseconds)'],
      ['AI Reasoning', 'Transmitting Frame for Analysis', 'Encrypted TLS 1.3 to AI Core', 'ZERO (Discarded after compute)'],
      ['Metadata Return', 'JSON Findings (Coordinates)', 'Local State Management', 'Short-term (Until Save)'],
      ['Final Archive', 'PDF / CSV / ZIP Generation', 'User-Selected Local Directory', 'Long-term (Permanent)']
    ],
    theme: 'grid',
    ...geaTableStyles
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;
  addSectionTitle(doc, "4.0 Storage Taxonomy", yPos);

  autoTable(doc, {
    startY: yPos + 10,
    head: [['Storage Type', 'What is stored?', 'Description']],
    body: [
      ['Short-term', 'Video Frames / Logs', 'Exists only while the browser tab is open. Once you refresh or close the tab, the high-resolution media is wiped from the browser state.'],
      ['Medium-term', 'Project Metadata', 'Stored in IndexedDB/LocalStorage. Contains project names and inspection summaries (no high-res video).'],
      ['Long-term', 'Exported Reports', 'Final PDF reports and ZIP evidence packages. These are only stored on your physical hard drive or NAS after you click "Export".']
    ],
    theme: 'striped',
    ...geaTableStyles
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;
  addCallout(doc, "CRITICAL", "NO SERVER-SIDE RESIDENCY", "GEA Group and its developers cannot see your inspection data. There is no 'Admin Panel' in the cloud where your videos reside. You are the sole custodian of the data from ingestion to archive.", yPos);

  addFooter(doc, absolutePage++);

  triggerDownload(doc, "GEA_NDT_CHECK_PRO_V6.0_GDPR_Data_Flow_Standards.pdf");
};
